/**
 * Parent-side text editing orchestrator. Manages the editing lifecycle:
 * element-clicked → beginTransaction → enable-editing → content-changed →
 * editing-complete → commitTransaction, editing-cancelled → abortTransaction.
 * Also relays text-selection formatting state to the text glass panel.
 */

import { store } from '../core/state.js';
import { eventBus } from '../core/events.js';
import { bridge } from './bridge.js';
import { sanitize } from '../core/sanitizer.js';
import { reportError } from '../core/report-error.js';
import {
  findEditableByIndex,
  injectEditableAttrs,
  stripTempAttrs,
  updateEditableByEidInHTML
} from './editable-utils.js';
import {
  CLIENT_LINK_BINDINGS,
  CLIENT_LINK_MARKER_PREFIX,
  CLIENT_TEXT_BINDINGS,
  resolveClientLinkHref,
  resolveClientText
} from '../shared/client-info.js';
import {
  openLinkPopover,
  closeLinkPopover,
  positionLinkPopover,
  isLinkPopoverOpen
} from './link-popover.js';
import { closeBindMenu, isBindMenuOpen, openBindMenu, positionBindMenu } from './bind-menu.js';
import { closeSlashMenu, isSlashMenuOpen, openSlashMenu, positionSlashMenu } from './slash-menu.js';
import { closeAIRewriteModal, openAIRewriteModal } from './ai-rewrite.js';
import {
  isSectionLockedByPeer,
  notifyLockedDenied,
  acquireSectionLock,
  releaseSectionLock,
  lockKeyForInstance,
  lockKeyForShared
} from './collab-guard.js';
import { isDomTarget } from '../shared/target-ref.js';

// Collab Phase 9: keep a peer-locked section from opening a text edit at all.
// Solo/no-collab: the guard is allow-all no-ops, so this is inert.
const LOCK_RELEASE_LINGER_MS = 1500;

function sectionLockKeyFor(instanceId) {
  const state = store.getState();
  if (state?.sharedSections?.navigation?.instanceId === instanceId)
    return lockKeyForShared('navigation');
  if (state?.sharedSections?.footer?.instanceId === instanceId) return lockKeyForShared('footer');
  return lockKeyForInstance(instanceId);
}

let activeEdit = null; // { instanceId, editableIndex, editableType, eid, editSessionId, caret, tagName, lockKey }
let lastCompletedTextEditMeta = null; // { tagName, eid, editSessionId, committedAt }
let boundChip = null;
let pasteTriageToast = null;
let activeBoundInfo = null;
let _pasteToastHideTimer = null;
let _nextEditSessionId = 1;
let _lastSelectionData = null;
let _bulkFormattingEnabled = false;
// Injected by shell.initTextEditor({ flushPendingEdit }). Drains the iframe's
// debounced content-changed for a session into state before a commit boundary.
let _flushPendingEdit = null;
let _contentOnlyMode = false;
// Monotonic counter so overlapping click-away flushes resolve latest-wins.
let _clickAwayToken = 0;
const _pendingTextRequests = new Set();
const RECENT_CARET_WINDOW_MS = 15000;
const _recentCaretByEid = new Map();
const PASTE_PROFILE_SEQUENCE = ['keep-formatting', 'match-site-style', 'plain-text'];
const PASTE_PROFILE_LABELS = Object.freeze({
  'keep-formatting': 'Keep formatting',
  'match-site-style': 'Match site style',
  'plain-text': 'Plain text'
});
const INLINE_COLOR_CLASS_BY_TOKEN = Object.freeze({
  accent: 'pb-inline-color-accent',
  heading: 'pb-inline-color-heading',
  body: 'pb-inline-color-body'
});
const INLINE_COLOR_CLASSES = Object.values(INLINE_COLOR_CLASS_BY_TOKEN);
const TEXT_BINDING_LABELS = Object.fromEntries(
  CLIENT_TEXT_BINDINGS.map((binding) => [binding.key, binding.label])
);
const LINK_BINDING_LABELS = Object.fromEntries(
  CLIENT_LINK_BINDINGS.map((binding) => [binding.key, binding.label])
);
const CLIENT_FIELD_PATH_BY_KEY = Object.freeze({
  name: 'clientInfo.name',
  tagline: 'clientInfo.tagline',
  email: 'clientInfo.email',
  phone: 'clientInfo.phone',
  phoneFormatted: 'clientInfo.phoneFormatted',
  domain: 'clientInfo.domain',
  addressLineOne: 'clientInfo.address.lineOne',
  addressLineTwo: 'clientInfo.address.lineTwo',
  addressCity: 'clientInfo.address.city',
  addressCounty: 'clientInfo.address.county',
  addressPostcode: 'clientInfo.address.postcode',
  addressBlock: 'clientInfo.address.lineOne',
  mapLink: 'clientInfo.address.mapLink',
  facebook: 'clientInfo.social.facebook',
  instagram: 'clientInfo.social.instagram',
  linkedin: 'clientInfo.social.linkedin',
  x: 'clientInfo.social.x',
  youtube: 'clientInfo.social.youtube',
  yelp: 'clientInfo.social.yelp'
});
const PROTECTED_TEXT_CHILD_SELECTOR = [
  'img',
  'picture',
  'svg',
  'canvas',
  'video',
  'audio',
  // Embedded form controls: a .cs-label / <label> is a text-editable but wraps an
  // <input>; the whitelist sanitizer strips these, so they must be restored by
  // signature (never widen the sanitizer whitelist — that would let paste inject them).
  'input',
  'textarea',
  'select',
  'option',
  'optgroup',
  'button',
  'fieldset',
  'legend',
  'output',
  'progress',
  'meter',
  'datalist',
  '.cs-icon',
  '.cs-icon-wrapper',
  '[aria-hidden="true"]',
  '[class*="icon"]'
].join(',');
const PROTECTED_TEXT_SLOT_ATTR = 'data-pagy-protected-slot';
const TEXT_ROOT_ATTR = 'data-pb-text-root';
const EDITABLE_TEXT_TAGS = new Set([
  'P',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'DIV',
  'UL',
  'OL',
  'BLOCKQUOTE'
]);

export function initTextEditor(deps = {}) {
  _flushPendingEdit = typeof deps.flushPendingEdit === 'function' ? deps.flushPendingEdit : null;
  _contentOnlyMode = deps.mode === 'content-only';
  eventBus.on('iframe:element-clicked', onElementClicked);
  eventBus.on('iframe:text-edit-requested', onTextEditRequested);
  eventBus.on('iframe:content-changed', onContentChanged);
  eventBus.on('iframe:editing-complete', onEditingComplete);
  eventBus.on('iframe:editing-cancelled', onEditingCancelled);
  eventBus.on('iframe:text-selected', onTextSelected);
  eventBus.on('iframe:selection-cleared', onSelectionCleared);
  eventBus.on('editable-ids:repaired', onEditableIdsRepaired);
  if (!_contentOnlyMode) {
    eventBus.on('iframe:bound-element-info', onBoundElementInfo);
    eventBus.on('iframe:request-bind-menu', onRequestBindMenu);
    eventBus.on('iframe:request-slash-menu', onRequestSlashMenu);
    eventBus.on('iframe:request-link-popover', onRequestLinkPopover);
    eventBus.on('iframe:paste-triage-status', onPasteTriageStatus);
    eventBus.on('editing:text-bulk-format', onBulkFormatModeChanged);
    createBoundChip();
  }
}

export function destroyTextEditor() {
  for (const request of [..._pendingTextRequests]) request.cancel();
  eventBus.off('iframe:element-clicked', onElementClicked);
  eventBus.off('iframe:text-edit-requested', onTextEditRequested);
  eventBus.off('iframe:content-changed', onContentChanged);
  eventBus.off('iframe:editing-complete', onEditingComplete);
  eventBus.off('iframe:editing-cancelled', onEditingCancelled);
  eventBus.off('iframe:text-selected', onTextSelected);
  eventBus.off('iframe:selection-cleared', onSelectionCleared);
  eventBus.off('editable-ids:repaired', onEditableIdsRepaired);
  eventBus.off('iframe:bound-element-info', onBoundElementInfo);
  eventBus.off('iframe:request-bind-menu', onRequestBindMenu);
  eventBus.off('iframe:request-slash-menu', onRequestSlashMenu);
  eventBus.off('iframe:request-link-popover', onRequestLinkPopover);
  eventBus.off('iframe:paste-triage-status', onPasteTriageStatus);
  eventBus.off('editing:text-bulk-format', onBulkFormatModeChanged);
  closeBindMenu();
  closeSlashMenu();
  removeBoundChip();
  removePasteTriageToast();
  closeLinkPopover();
  closeAIRewriteModal();
  _flushPendingEdit = null;
  _contentOnlyMode = false;
}

function onElementClicked(data) {
  if (!isDomTarget(data)) return;
  if (data.editableType !== 'text') return;
  if (_contentOnlyMode && data.clientLocked) return;
  // The iframe explicitly marks ordinary clicks as selection-only. Payloads
  // without this flag remain backward-compatible with older callers/tests.
  if (data.editIntent === false) return;
  const incomingEid = typeof data.eid === 'string' && data.eid ? data.eid : null;

  // If clicking the same element we're already editing, do nothing (avoid restarting transaction)
  const sameByEid = !!(
    activeEdit &&
    incomingEid &&
    activeEdit.eid &&
    activeEdit.instanceId === data.instanceId &&
    activeEdit.eid === incomingEid
  );
  const sameByIndex = !!(
    activeEdit &&
    !incomingEid &&
    !activeEdit.eid &&
    activeEdit.instanceId === data.instanceId &&
    activeEdit.editableIndex === data.editableIndex
  );
  if (sameByEid || sameByIndex) {
    return;
  }

  // Switching to a different element while one is being edited: drain the
  // previous element's in-flight debounced edit (stamped with the OLD session
  // id) into state BEFORE committing, or the last ~300ms of typing is dropped as
  // stale. flushPendingEdit is async, so hand off to a helper that re-checks
  // currency after the await. Only when the flush is wired (production); tests
  // that call initTextEditor() without deps keep the synchronous path.
  if (activeEdit && _flushPendingEdit) {
    // Monotonic token so that if several click-aways queue during one flush, the
    // LATEST wins and earlier continuations bail. .catch keeps a rejected
    // continuation from surfacing as an unhandled rejection (eventBus.emit is
    // fire-and-forget and only try/catches synchronous throws).
    const token = ++_clickAwayToken;
    _beginEditAfterFlush(data, incomingEid, activeEdit, token).catch(() => {});
    return;
  }

  _beginEditNow(data, incomingEid);
}

function onTextEditRequested(data) {
  if (!data || data.editableType !== 'text') return;
  onElementClicked({ ...data, editIntent: true });
}

async function _beginEditAfterFlush(data, incomingEid, previousEdit, token) {
  try {
    await _flushPendingEdit(previousEdit.editSessionId);
  } catch {
    // Flush timed out or errored — fall through and commit what we already have.
  }
  // A newer click-away superseded this one → bail (the latest click wins).
  if (token !== _clickAwayToken) return;
  // Currency guard: if the active edit changed while awaiting (cancel/completion
  // already handled the previous session), bail so we neither double-commit nor
  // clobber a newer edit.
  if (activeEdit !== previousEdit) return;
  _beginEditNow(data, incomingEid);
}

function _beginEditNow(data, incomingEid) {
  // If editing a different element, commit the previous transaction first
  if (activeEdit) {
    store.commitTransaction();
  }

  // Collab Phase 9: a peer already editing this section blocks entry (optimistic
  // acquire otherwise; a lost race rolls back later as op-rejected lock_held).
  const lockKey = sectionLockKeyFor(data.instanceId);
  if (isSectionLockedByPeer(lockKey)) {
    notifyLockedDenied(lockKey);
    return;
  }

  // Programmatic/stale iframe clicks can arrive just after this tab becomes
  // secondary. Do not create a parent edit session unless the store accepted
  // the transaction; the read-only rejection is surfaced centrally by shell.
  if (!store.beginTransaction('Edit text')) return;
  acquireSectionLock(lockKey);

  const editSessionId = _nextEditSessionId++;
  const recentCaret = incomingEid ? _takeRecentCaret(data.instanceId, incomingEid) : null;

  activeEdit = {
    instanceId: data.instanceId,
    editableIndex: data.editableIndex,
    editableType: data.editableType,
    eid: incomingEid,
    editSessionId,
    caret: recentCaret,
    tagName: _normalizeTagName(data.tagName),
    lockKey
  };
  activeBoundInfo = null;
  _bulkFormattingEnabled = false;
  hideBoundChip();
  closeBindMenu();
  closeSlashMenu();

  // Tell iframe to make the element contenteditable
  bridge.send('enable-editing', {
    instanceId: data.instanceId,
    editableIndex: data.editableIndex,
    eid: incomingEid,
    editSessionId,
    caret: recentCaret,
    plainTextOnly: _contentOnlyMode
  });

  // Emit for property panel to show text editing info
  eventBus.emit('editing:text-started', activeEdit);
}

/**
 * Flush and finish the current iframe text edit before a parent-side scope
 * change (page switch, tab ownership handoff). The flush ACK is ordered after
 * content-changed, so committing here includes the user's final keystrokes.
 */
export async function commitActiveTextEdit() {
  const pendingEdit = activeEdit;
  if (!pendingEdit) {
    store.commitTransaction();
    return false;
  }

  if (_flushPendingEdit) {
    try {
      await _flushPendingEdit(pendingEdit.editSessionId);
    } catch {
      // Commit the latest content already received if the iframe cannot ACK.
    }
  }
  if (activeEdit !== pendingEdit) return true;

  onEditingComplete({
    instanceId: pendingEdit.instanceId,
    eid: pendingEdit.eid,
    editSessionId: pendingEdit.editSessionId,
    tagName: pendingEdit.tagName
  });
  bridge.send('disable-editing', {});
  return true;
}

/** Cancel any parent-side edit session without accepting iframe-only DOM. */
export function cancelActiveTextEdit() {
  const pendingEdit = activeEdit;
  if (!pendingEdit) return false;
  onEditingCancelled({
    instanceId: pendingEdit.instanceId,
    eid: pendingEdit.eid,
    editSessionId: pendingEdit.editSessionId,
    tagName: pendingEdit.tagName
  });
  bridge.send('disable-editing', {});
  return true;
}

/**
 * Resolve an instanceId to its section data and state path.
 * Checks both page sections and shared sections (nav/footer).
 */
function resolveSectionByInstance(instanceId) {
  const state = store.getState();

  // Check shared sections first
  for (const type of ['navigation', 'footer']) {
    const shared = state.sharedSections[type];
    if (shared && shared.instanceId === instanceId) {
      return { section: shared, path: `sharedSections.${type}.html` };
    }
  }

  // Check active page sections
  const page = store.getActivePage();
  if (!page) return null;

  const sectionIndex = page.sections.findIndex((s) => s.instanceId === instanceId);
  if (sectionIndex === -1) return null;

  const pageIndex = state.pages.indexOf(page);
  return {
    section: page.sections[sectionIndex],
    path: `pages.${pageIndex}.sections.${sectionIndex}.html`
  };
}

function onContentChanged(data) {
  if (!activeEdit) return;
  if (data.instanceId !== activeEdit.instanceId) return;
  if (typeof data.editSessionId === 'number' && data.editSessionId !== activeEdit.editSessionId) {
    console.warn('[TextEditor] Dropping stale content-changed: editSessionId mismatch', {
      expected: activeEdit.editSessionId,
      received: data.editSessionId
    });
    return;
  }
  if (data.eid && activeEdit.eid && data.eid !== activeEdit.eid) {
    console.warn('[TextEditor] Dropping content-changed: eid mismatch', {
      expected: activeEdit.eid,
      received: data.eid
    });
    return;
  }
  if (!activeEdit.eid && data.eid) activeEdit.eid = data.eid;
  if (data.tagName) activeEdit.tagName = _normalizeTagName(data.tagName);
  if (data.caret) activeEdit.caret = data.caret;

  const resolved = resolveSectionByInstance(data.instanceId);
  if (!resolved) return;

  const updatedHtml = updateEditableInHTML(
    resolved.section.html,
    data.editableIndex,
    data.eid || activeEdit.eid,
    typeof data.rawHtml === 'string' ? data.rawHtml : data.html,
    data.tagName,
    data.removedProtectedPaths
  );
  if (updatedHtml === null) return;

  store.update(resolved.path, updatedHtml, 'Edit text');
}

function childSignature(node) {
  if (!node || node.nodeType !== Node.ELEMENT_NODE) return '';
  const tag = node.tagName.toLowerCase();
  const cls = node.getAttribute('class') || '';
  const src = node.getAttribute('src') || node.getAttribute('srcset') || '';
  const aria = node.getAttribute('aria-hidden') || '';
  // Form-control discriminators: two sibling controls can share a class
  // (e.g. two .cs-input), so tag|class|src|aria alone collides and one would be
  // dropped as a duplicate. Fold in the identity/value attributes.
  const type = node.getAttribute('type') || '';
  const name = node.getAttribute('name') || '';
  const id = node.getAttribute('id') || '';
  const value = node.getAttribute('value') || '';
  const placeholder = node.getAttribute('placeholder') || '';
  const checked = node.hasAttribute('checked') ? '1' : '';
  return `${tag}|${cls}|${src}|${aria}|${type}|${name}|${id}|${value}|${placeholder}|${checked}`;
}

function _protectedRoots(root) {
  if (!root?.querySelectorAll) return [];
  return Array.from(root.querySelectorAll(PROTECTED_TEXT_CHILD_SELECTOR)).filter((node) => {
    let parent = node.parentElement;
    while (parent && parent !== root) {
      if (parent.matches?.(PROTECTED_TEXT_CHILD_SELECTOR)) return false;
      parent = parent.parentElement;
    }
    return true;
  });
}

function _childNodePath(root, node) {
  const path = [];
  let current = node;
  while (current && current !== root) {
    const parent = current.parentNode;
    if (!parent) return null;
    path.unshift(Array.prototype.indexOf.call(parent.childNodes, current));
    current = parent;
  }
  return current === root ? path : null;
}

function _nodeAtPath(root, path) {
  let current = root;
  for (const index of path || []) {
    current = current?.childNodes?.[index] || null;
    if (!current) return null;
  }
  return current;
}

function _firstStableEid(node) {
  return (
    node?.getAttribute?.('data-pb-eid') ||
    node?.querySelector?.('[data-pb-eid]')?.getAttribute('data-pb-eid') ||
    ''
  );
}

function _findIncomingProtectedRoot(incomingRoot, originalNode, claimed) {
  const eid = _firstStableEid(originalNode);
  if (eid) {
    const escaped =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape(eid)
        : eid.replace(/[^A-Za-z0-9_-]/g, (ch) => `\\${ch}`);
    const eidNode = incomingRoot.querySelector(`[data-pb-eid="${escaped}"]`);
    if (eidNode) {
      const protectedNode = eidNode.matches?.(PROTECTED_TEXT_CHILD_SELECTOR)
        ? eidNode
        : eidNode.closest?.(PROTECTED_TEXT_CHILD_SELECTOR);
      if (protectedNode && incomingRoot.contains(protectedNode) && !claimed.has(protectedNode)) {
        return protectedNode;
      }
    }
  }

  const signature = childSignature(originalNode);
  return (
    _protectedRoots(incomingRoot).find(
      (candidate) => !claimed.has(candidate) && childSignature(candidate) === signature
    ) || null
  );
}

function _insertMissingProtectedSlot(incomingRoot, originalTarget, originalNode, slot) {
  const path = _childNodePath(originalTarget, originalNode);
  if (!path?.length) {
    incomingRoot.appendChild(slot);
    return;
  }
  const parent = _nodeAtPath(incomingRoot, path.slice(0, -1));
  const host = parent?.nodeType === Node.ELEMENT_NODE ? parent : incomingRoot;
  host.insertBefore(slot, host.childNodes[path[path.length - 1]] || null);
}

/**
 * Sanitize edited text while token-protecting every pre-existing nested media,
 * icon, and form subtree. Pasted replacements never enter the trusted map.
 */
function sanitizeAndRestoreProtectedTextChildren(target, originalTarget, rawHtml, options = {}) {
  if (!target || !originalTarget) return;
  const doc = target.ownerDocument;
  const incomingRoot = doc.createElement('div');
  incomingRoot.innerHTML = typeof rawHtml === 'string' ? rawHtml : target.innerHTML;
  for (const spoofed of incomingRoot.querySelectorAll(`[${PROTECTED_TEXT_SLOT_ATTR}]`)) {
    spoofed.removeAttribute(PROTECTED_TEXT_SLOT_ATTR);
  }

  const originals = _protectedRoots(originalTarget);
  const removedPaths = new Set(
    (Array.isArray(options.removedProtectedPaths) ? options.removedProtectedPaths : [])
      .filter(Array.isArray)
      .map((path) => path.join('.'))
  );
  const claimed = new Set();
  const trustedBySlot = new Map();
  originals.forEach((originalNode, index) => {
    const originalPath = _childNodePath(originalTarget, originalNode);
    if (originalPath && removedPaths.has(originalPath.join('.'))) return;
    const slotId = `protected-${index}`;
    const slot = doc.createElement('span');
    slot.setAttribute(PROTECTED_TEXT_SLOT_ATTR, slotId);
    trustedBySlot.set(slotId, originalNode.cloneNode(true));
    const incomingNode = _findIncomingProtectedRoot(incomingRoot, originalNode, claimed);
    if (incomingNode) {
      claimed.add(incomingNode);
      incomingNode.replaceWith(slot);
    } else {
      _insertMissingProtectedSlot(incomingRoot, originalTarget, originalNode, slot);
    }
  });

  const sanitizedRoot = doc.createElement('div');
  sanitizedRoot.innerHTML = sanitize(incomingRoot.innerHTML);
  for (const slot of sanitizedRoot.querySelectorAll(`[${PROTECTED_TEXT_SLOT_ATTR}]`)) {
    const trusted = trustedBySlot.get(slot.getAttribute(PROTECTED_TEXT_SLOT_ATTR));
    if (trusted) slot.replaceWith(trusted);
    else slot.removeAttribute(PROTECTED_TEXT_SLOT_ATTR);
  }
  target.replaceChildren(...Array.from(sanitizedRoot.childNodes));
}

function restoreProtectedTextChildren(target, originalTarget, options = {}) {
  if (!target || !originalTarget) return;
  sanitizeAndRestoreProtectedTextChildren(target, originalTarget, target.innerHTML, options);
}

function retagEditableTarget(target, newTagName) {
  if (!target?.parentNode) return target;
  const desired = String(newTagName || '')
    .trim()
    .toUpperCase();
  if (!EDITABLE_TEXT_TAGS.has(desired) || target.tagName === desired) return target;
  const replacement = target.ownerDocument.createElement(desired.toLowerCase());
  for (const attr of Array.from(target.attributes || [])) {
    replacement.setAttribute(attr.name, attr.value);
  }
  while (target.firstChild) replacement.appendChild(target.firstChild);
  if (['UL', 'OL', 'DIV', 'BLOCKQUOTE'].includes(desired)) {
    replacement.setAttribute(TEXT_ROOT_ATTR, '');
  } else {
    replacement.removeAttribute(TEXT_ROOT_ATTR);
  }
  target.parentNode.replaceChild(replacement, target);
  return replacement;
}

function onEditingComplete(data) {
  if (!activeEdit) return;
  if (typeof data?.editSessionId === 'number' && data.editSessionId !== activeEdit.editSessionId)
    return;
  if (data?.caret) activeEdit.caret = data.caret;
  if (activeEdit.eid && activeEdit.caret)
    _rememberRecentCaret(activeEdit.instanceId, activeEdit.eid, activeEdit.caret);

  const completedEdit = {
    ...activeEdit,
    tagName: _normalizeTagName(data?.tagName || activeEdit.tagName)
  };

  lastCompletedTextEditMeta = {
    instanceId: completedEdit.instanceId,
    tagName: completedEdit.tagName,
    eid: completedEdit.eid || data?.eid || null,
    editSessionId: completedEdit.editSessionId,
    committedAt: Date.now()
  };

  store.commitTransaction();
  releaseLockWithLinger(completedEdit.lockKey);
  activeEdit = null;
  activeBoundInfo = null;
  _lastSelectionData = null;
  _bulkFormattingEnabled = false;
  eventBus.emit('editing:selection-state', null);
  hideBoundChip();
  hidePasteTriageToast();
  closeLinkPopover();
  closeBindMenu();
  closeSlashMenu();
  closeAIRewriteModal();

  eventBus.emit('editing:text-ended', {
    ...(data || {}),
    eid: completedEdit.eid || data?.eid || null,
    editSessionId: completedEdit.editSessionId,
    tagName: completedEdit.tagName,
    cancelled: false
  });
}

// Collab Phase 9: hold the lock a beat past blur so a quick re-click by the same
// user doesn't thrash acquire/release (and a peer doesn't briefly steal it).
function releaseLockWithLinger(lockKey) {
  if (!lockKey) return;
  setTimeout(() => releaseSectionLock(lockKey), LOCK_RELEASE_LINGER_MS);
}

function onEditingCancelled(data) {
  if (!activeEdit) return;
  if (typeof data?.editSessionId === 'number' && data.editSessionId !== activeEdit.editSessionId)
    return;
  if (data?.caret) activeEdit.caret = data.caret;
  if (activeEdit.eid && activeEdit.caret)
    _rememberRecentCaret(activeEdit.instanceId, activeEdit.eid, activeEdit.caret);
  const cancelledEdit = activeEdit;

  store.abortTransaction();
  releaseLockWithLinger(cancelledEdit.lockKey);
  activeEdit = null;
  activeBoundInfo = null;
  _lastSelectionData = null;
  _bulkFormattingEnabled = false;
  eventBus.emit('editing:selection-state', null);
  hideBoundChip();
  hidePasteTriageToast();
  closeLinkPopover();
  closeBindMenu();
  closeSlashMenu();
  closeAIRewriteModal();

  eventBus.emit('editing:text-ended', {
    ...(data || {}),
    eid: cancelledEdit.eid || data?.eid || null,
    editSessionId: cancelledEdit.editSessionId,
    tagName: _normalizeTagName(data?.tagName || cancelledEdit.tagName),
    cancelled: true
  });
}

function onEditableIdsRepaired({ instanceIds = [] } = {}) {
  for (const request of [..._pendingTextRequests]) {
    if (!request.instanceId || instanceIds.includes(request.instanceId)) request.cancel();
  }
  for (const [key, value] of _recentCaretByEid) {
    if (instanceIds.includes(value.instanceId)) _recentCaretByEid.delete(key);
  }
  if (instanceIds.includes(lastCompletedTextEditMeta?.instanceId)) lastCompletedTextEditMeta = null;
  if (!instanceIds.includes(activeEdit?.instanceId)) return;
  const retired = activeEdit;
  // The repair/history owner already settled state. Retire only this session;
  // aborting or committing here could affect a different owner's transaction.
  activeEdit = null;
  _clickAwayToken++;
  activeBoundInfo = null;
  _lastSelectionData = null;
  _bulkFormattingEnabled = false;
  releaseLockWithLinger(retired.lockKey);
  hideBoundChip();
  hidePasteTriageToast();
  closeLinkPopover();
  closeBindMenu();
  closeSlashMenu();
  closeAIRewriteModal();
  bridge.send('disable-editing', {});
  eventBus.emit('editing:selection-state', null);
  eventBus.emit('editing:text-ended', {
    ...retired,
    cancelled: true,
    reason: 'editable-id-repair'
  });
}

function onTextSelected(data) {
  if (!activeEdit) return;
  if (data?.eid && activeEdit.eid && data.eid !== activeEdit.eid) return;

  _lastSelectionData = data;
  eventBus.emit('editing:selection-state', data);

  if (isLinkPopoverOpen()) {
    const anchorRect = getSelectionAnchorRect();
    if (anchorRect) positionLinkPopover(anchorRect);
  }
  if (isBindMenuOpen()) {
    const anchorRect = getSelectionAnchorRect();
    if (anchorRect) positionBindMenu(anchorRect);
  }
  if (isSlashMenuOpen()) {
    const anchorRect = getSelectionAnchorRect();
    if (anchorRect) positionSlashMenu(anchorRect);
  }
  if (activeBoundInfo) {
    showBoundChip(activeBoundInfo);
  }
}

function onSelectionCleared() {
  _lastSelectionData = null;
  activeBoundInfo = null;
  eventBus.emit('editing:selection-state', null);
  hideBoundChip();
  closeBindMenu();
  closeSlashMenu();
  if (isLinkPopoverOpen()) closeLinkPopover();
}

function onBoundElementInfo(data) {
  if (!activeEdit) return;
  if (!data) {
    activeBoundInfo = null;
    hideBoundChip();
    return;
  }
  if (typeof data.editSessionId === 'number' && data.editSessionId !== activeEdit.editSessionId)
    return;
  if (data.eid && activeEdit.eid && data.eid !== activeEdit.eid) return;

  const binding = resolveBindingFromStoredHTML({
    instanceId: activeEdit.instanceId,
    eid: data.eid || activeEdit.eid,
    editableIndex: activeEdit.editableIndex
  });
  activeBoundInfo = binding;
  if (binding) showBoundChip(binding);
  else hideBoundChip();
}

function onRequestBindMenu(data) {
  if (!activeEdit) return;
  if (typeof data?.editSessionId === 'number' && data.editSessionId !== activeEdit.editSessionId)
    return;
  if (data?.eid && activeEdit.eid && data.eid !== activeEdit.eid) return;
  closeLinkPopover();

  const rect = data?.rect;
  const anchorRect =
    rect && typeof rect.left === 'number' && typeof rect.top === 'number'
      ? {
          left: rect.left,
          top: rect.top,
          width: Number(rect.width || 0),
          height: Number(rect.height || 0),
          right: rect.left + Number(rect.width || 0),
          bottom: rect.top + Number(rect.height || 0)
        }
      : getSelectionAnchorRect();
  if (!anchorRect) return;

  const selectionText = data?.selectionText || '';
  const sniff = selectionText.replace(/\s+/g, ' ').trim().slice(0, 24);
  const extraItems = sniff
    ? [
        {
          label: `🔍 Search & replace "${sniff}"…`,
          skipRestoreFocus: true,
          onSelect: () => {
            eventBus.emit('find-replace:open', {
              initialQuery: selectionText,
              expandReplace: true
            });
          }
        }
      ]
    : null;

  openBindMenu({
    anchorRect,
    selectionText,
    extraItems,
    restoreFocus: restoreActiveEditableFocus,
    onSelect: (choice) => {
      const inferredKind = _lastSelectionData?.link ? 'anchor-text' : 'span';
      bridge.send('wrap-selection-with-binding', {
        editSessionId: activeEdit.editSessionId,
        bindingKey: choice.key,
        kind: inferredKind
      });
      activeBoundInfo = {
        kind: 'text',
        bindingKey: choice.key,
        label: choice.label
      };
      showBoundChip(activeBoundInfo);
    }
  });
}

function onRequestSlashMenu(data) {
  if (!activeEdit || !data) return;
  if (typeof data.editSessionId === 'number' && data.editSessionId !== activeEdit.editSessionId)
    return;
  if (data.eid && activeEdit.eid && data.eid !== activeEdit.eid) return;

  const rect = data.rect;
  const anchorRect =
    rect && typeof rect.left === 'number' && typeof rect.top === 'number'
      ? {
          left: rect.left,
          top: rect.top,
          width: Number(rect.width || 0),
          height: Number(rect.height || 0),
          right: rect.left + Number(rect.width || 0),
          bottom: rect.top + Number(rect.height || 0)
        }
      : getSelectionAnchorRect();
  if (!anchorRect) return;

  closeBindMenu();
  closeLinkPopover();

  openSlashMenu({
    anchorRect,
    restoreFocus: restoreActiveEditableFocus,
    commands: [
      { id: 'h1', label: '/h1', description: 'Heading 1' },
      { id: 'h2', label: '/h2', description: 'Heading 2' },
      { id: 'h3', label: '/h3', description: 'Heading 3' },
      { id: 'bullet', label: '/bullet', description: 'Bullet list' },
      { id: 'link', label: '/link', description: 'Insert or edit link' },
      { id: 'emoji', label: '/emoji', description: 'Insert emoji' },
      { id: 'phone', label: '/phone', description: 'Bind to client phone' },
      { id: 'email', label: '/email', description: 'Bind to client email' },
      { id: 'address', label: '/address', description: 'Bind to client address' },
      { id: 'divider', label: '/divider', description: 'Insert divider' },
      { id: 'ai', label: '/ai', description: 'AI rewrite', disabled: !isAIRewriteEnabled() }
    ],
    onSelect: (command) => {
      if (!command) return;
      if (command.id === 'link') {
        handleLinkFormat();
        return;
      }
      if (command.id === 'ai') {
        void openAIRewriteFlow();
        return;
      }
      bridge.send('apply-slash-command', {
        editSessionId: activeEdit ? activeEdit.editSessionId : null,
        command: command.id
      });
    }
  });
}

function onRequestLinkPopover(data) {
  if (!activeEdit) return;
  if (typeof data?.editSessionId === 'number' && data.editSessionId !== activeEdit.editSessionId)
    return;
  handleLinkFormat();
}

function onBulkFormatModeChanged(payload) {
  if (!activeEdit) {
    _bulkFormattingEnabled = false;
    return;
  }
  if (
    typeof payload?.editSessionId === 'number' &&
    payload.editSessionId !== activeEdit.editSessionId
  )
    return;
  if (payload?.eid && activeEdit.eid && payload.eid !== activeEdit.eid) return;
  _bulkFormattingEnabled = !!payload?.enabled;
}

function onPasteTriageStatus(data) {
  if (!activeEdit || !data) return;
  if (typeof data.editSessionId === 'number' && data.editSessionId !== activeEdit.editSessionId)
    return;
  if (data.eid && activeEdit.eid && data.eid !== activeEdit.eid) return;

  const profile = PASTE_PROFILE_SEQUENCE.includes(data.profile) ? data.profile : 'match-site-style';
  showPasteTriageToast({
    profile,
    rejected: !!data.rejected,
    truncated: !!data.truncated,
    reason: String(data.reason || '')
  });
}

function parseClientBindingKey(href) {
  const value = String(href || '').trim();
  if (!value || !value.startsWith(CLIENT_LINK_MARKER_PREFIX)) return '';
  return value.slice(CLIENT_LINK_MARKER_PREFIX.length).trim();
}

function bindingLabelForKey(bindingKey) {
  return TEXT_BINDING_LABELS[bindingKey] || LINK_BINDING_LABELS[bindingKey] || bindingKey;
}

function escapeSelectorValue(value) {
  if (typeof globalThis.CSS?.escape === 'function') return globalThis.CSS.escape(value);
  return String(value || '').replace(/["\\]/g, '\\$&');
}

function unwrapElementPreservingChildren(node) {
  if (!node || !node.parentNode) return;
  const parent = node.parentNode;
  while (node.firstChild) {
    parent.insertBefore(node.firstChild, node);
  }
  parent.removeChild(node);
}

function resolveBindingFromStoredHTML({ instanceId, eid, editableIndex }) {
  const resolved = resolveSectionByInstance(instanceId);
  const sectionHtml = resolved?.section?.html;
  if (!sectionHtml) return null;

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${sectionHtml}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return null;

  injectEditableAttrs(root);
  let target = null;
  if (eid) target = root.querySelector(`[data-pb-eid="${eid}"]`);
  if (!target && typeof editableIndex === 'number') {
    const allEditables = root.querySelectorAll('[data-pb-editable]');
    target = allEditables[editableIndex] || null;
  }
  if (!target) {
    stripTempAttrs(root);
    return null;
  }

  const anchorWithTextBinding = target.matches('a[data-pb-client-text]')
    ? target
    : target.querySelector('a[data-pb-client-text]');
  const textKeyFromAnchor = anchorWithTextBinding?.getAttribute('data-pb-client-text') || '';

  const textBindingNode = target.matches('[data-pb-client]')
    ? target
    : target.querySelector('[data-pb-client]');
  const textKeyFromSpan = textBindingNode?.getAttribute('data-pb-client') || '';

  const hrefAnchor = target.matches(`a[href^="${CLIENT_LINK_MARKER_PREFIX}"]`)
    ? target
    : target.querySelector(`a[href^="${CLIENT_LINK_MARKER_PREFIX}"]`);
  const hrefKey = parseClientBindingKey(hrefAnchor?.getAttribute('href') || '');

  stripTempAttrs(root);

  const textKey = textKeyFromAnchor || textKeyFromSpan || '';
  if (textKey && hrefKey) {
    return {
      kind: 'composite',
      bindingKey: textKey,
      textKey,
      hrefKey,
      label: bindingLabelForKey(textKey)
    };
  }
  if (textKey) {
    return {
      kind: 'text',
      bindingKey: textKey,
      textKey,
      label: bindingLabelForKey(textKey)
    };
  }
  if (hrefKey) {
    return {
      kind: 'href',
      bindingKey: hrefKey,
      hrefKey,
      label: bindingLabelForKey(hrefKey)
    };
  }
  return null;
}

function unbindActiveBinding() {
  if (!activeEdit || !activeBoundInfo) return;
  const resolved = resolveSectionByInstance(activeEdit.instanceId);
  const sectionHtml = resolved?.section?.html;
  if (!sectionHtml) return;

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${sectionHtml}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return;

  injectEditableAttrs(root);
  let target = null;
  if (activeEdit.eid) {
    target = root.querySelector(`[data-pb-eid="${activeEdit.eid}"]`);
  }
  if (!target && typeof activeEdit.editableIndex === 'number') {
    target = root.querySelectorAll('[data-pb-editable]')[activeEdit.editableIndex] || null;
  }
  if (!target) {
    stripTempAttrs(root);
    return;
  }

  let changed = false;
  const bindingKey = activeBoundInfo.bindingKey || '';

  if (activeBoundInfo.kind === 'text' || activeBoundInfo.kind === 'composite') {
    const escaped = escapeSelectorValue(bindingKey);
    const textAnchors = bindingKey
      ? target.querySelectorAll(`a[data-pb-client-text="${escaped}"]`)
      : target.querySelectorAll('a[data-pb-client-text]');
    if (
      target.matches('a[data-pb-client-text]') &&
      (!bindingKey || target.getAttribute('data-pb-client-text') === bindingKey)
    ) {
      target.removeAttribute('data-pb-client-text');
      changed = true;
    }
    for (const anchor of textAnchors) {
      anchor.removeAttribute('data-pb-client-text');
      changed = true;
    }

    if (
      target.hasAttribute('data-pb-client') &&
      (!bindingKey || target.getAttribute('data-pb-client') === bindingKey)
    ) {
      target.removeAttribute('data-pb-client');
      changed = true;
    }
    const spans = bindingKey
      ? target.querySelectorAll(`[data-pb-client="${escaped}"]`)
      : target.querySelectorAll('[data-pb-client]');
    for (const node of spans) {
      if (node === target) {
        node.removeAttribute('data-pb-client');
      } else {
        unwrapElementPreservingChildren(node);
      }
      changed = true;
    }
  }

  if (activeBoundInfo.kind === 'href' || activeBoundInfo.kind === 'composite') {
    const state = store.getState();
    const hrefAnchors = target.matches('a[href]')
      ? [target, ...target.querySelectorAll('a[href]')]
      : [...target.querySelectorAll('a[href]')];
    const hrefTargetKey = activeBoundInfo.hrefKey || bindingKey;
    for (const anchor of hrefAnchors) {
      const href = anchor.getAttribute('href') || '';
      const markerKey = parseClientBindingKey(href);
      if (!markerKey) continue;
      if (hrefTargetKey && markerKey !== hrefTargetKey) continue;
      const resolvedHref = resolveClientLinkHref(
        href,
        state.clientInfo,
        'preview',
        state.projectName || 'Untitled'
      );
      if (resolvedHref) anchor.setAttribute('href', resolvedHref);
      else anchor.removeAttribute('href');
      changed = true;
    }
  }

  if (!changed) {
    stripTempAttrs(root);
    return;
  }

  stripTempAttrs(root);
  store.update(resolved.path, root.innerHTML, 'Edit text');
  bridge.send('unwrap-binding', {
    editSessionId: activeEdit.editSessionId,
    eid: activeEdit.eid || null,
    bindingKey: bindingKey || null
  });

  activeBoundInfo = resolveBindingFromStoredHTML({
    instanceId: activeEdit.instanceId,
    eid: activeEdit.eid,
    editableIndex: activeEdit.editableIndex
  });
  if (activeBoundInfo) showBoundChip(activeBoundInfo);
  else hideBoundChip();
}

/**
 * Update a specific editable element's content within the section HTML string.
 * Stored HTML does NOT have data-pb-editable attributes (those are render-time only),
 * so we must inject them temporarily using the same selectors as iframe-renderer,
 * find the target by index, update it, then strip all injected attributes before storing.
 *
 * Auto-unbind: any data-pb-client / data-pb-client-text inside the updated
 * element whose textContent no longer matches the resolved binding value is
 * stripped. This keeps sitewide bindings from clobbering user edits on the
 * next cascade tick.
 */
function updateEditableInHTML(
  sectionHtml,
  editableIndex,
  editableEid,
  newInnerHTML,
  newTagName = '',
  removedProtectedPaths = []
) {
  if (editableEid) {
    const eidResult = updateEditableByEidInHTML(sectionHtml, editableEid, newInnerHTML, {
      onTargetUpdated: (target, originalTarget) => {
        restoreProtectedTextChildren(target, originalTarget, { removedProtectedPaths });
        if (target.getAttribute('data-pb-editable') === 'text') {
          unbindMismatchedClientBindings(target);
        }
        retagEditableTarget(target, newTagName);
      }
    });
    if (eidResult) {
      if (eidResult.editableType !== 'text') return null;
      return eidResult.html;
    }
  }

  const target = findEditableByIndex(sectionHtml, editableIndex);
  if (!target) return null;
  if (target.getAttribute('data-pb-editable') !== 'text') return null;

  const originalTarget = target.cloneNode(true);
  target.innerHTML = newInnerHTML;
  restoreProtectedTextChildren(target, originalTarget, { removedProtectedPaths });
  unbindMismatchedClientBindings(target);
  retagEditableTarget(target, newTagName);

  const root = target.ownerDocument.body.firstElementChild;
  stripTempAttrs(root);
  return root.innerHTML;
}

function _rememberRecentCaret(instanceId, eid, caret) {
  if (!eid || !caret) return;
  _recentCaretByEid.set(`${instanceId}::${eid}`, {
    instanceId,
    caret: structuredClone(caret),
    capturedAt: Date.now()
  });
}

function _takeRecentCaret(instanceId, eid) {
  if (!eid) return null;
  const key = `${instanceId}::${eid}`;
  const entry = _recentCaretByEid.get(key);
  if (!entry) return null;
  if (Date.now() - entry.capturedAt > RECENT_CARET_WINDOW_MS) {
    _recentCaretByEid.delete(key);
    return null;
  }
  return structuredClone(entry.caret);
}

function unbindMismatchedClientBindings(target) {
  const state = store.getState();
  const clientInfo = state?.clientInfo;
  const projectName = state?.projectName || 'Untitled';
  if (!clientInfo) return;

  const textBindingNodes = target.matches('[data-pb-client]')
    ? [target, ...target.querySelectorAll('[data-pb-client]')]
    : [...target.querySelectorAll('[data-pb-client]')];
  for (const el of textBindingNodes) {
    const key = el.getAttribute('data-pb-client');
    const expected = resolveClientText(key, clientInfo, 'preview', projectName);
    const actual = (el.textContent || '').trim();
    if (actual !== (expected || '').trim()) {
      el.removeAttribute('data-pb-client');
    }
  }
  const anchorBindingNodes = target.matches('a[data-pb-client-text]')
    ? [target, ...target.querySelectorAll('a[data-pb-client-text]')]
    : [...target.querySelectorAll('a[data-pb-client-text]')];
  for (const a of anchorBindingNodes) {
    const key = a.getAttribute('data-pb-client-text');
    const expected = resolveClientText(key, clientInfo, 'preview', projectName);
    const actual = (a.textContent || '').trim();
    if (actual !== (expected || '').trim()) {
      a.removeAttribute('data-pb-client-text');
    }
  }
}

function normalizeClassSignature(classValue) {
  return String(classValue || '')
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((cls) => !INLINE_COLOR_CLASSES.includes(cls))
    .sort()
    .join(' ');
}

function resolveBulkCriteria() {
  if (!activeEdit) return null;
  const resolved = resolveSectionByInstance(activeEdit.instanceId);
  const sectionHtml = resolved?.section?.html;
  if (!sectionHtml) return null;

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${sectionHtml}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return null;
  injectEditableAttrs(root);

  let target = null;
  if (activeEdit.eid) target = root.querySelector(`[data-pb-eid="${activeEdit.eid}"]`);
  if (!target && typeof activeEdit.editableIndex === 'number') {
    target = root.querySelectorAll('[data-pb-editable]')[activeEdit.editableIndex] || null;
  }
  if (!target) {
    stripTempAttrs(root);
    return null;
  }

  const criteria = {
    tagName: (target.tagName || '').toUpperCase(),
    classSignature: normalizeClassSignature(target.getAttribute('class') || '')
  };
  stripTempAttrs(root);
  return criteria;
}

function toggleWholeInlineTag(editable, tagName) {
  if (!editable) return false;
  const upperTag = String(tagName || '').toUpperCase();
  if (!upperTag) return false;

  const first = editable.firstElementChild;
  const onlyChildElement = !!first && editable.children.length === 1;
  const hasOnlyElementNode = onlyChildElement && editable.childNodes.length === 1;
  if (hasOnlyElementNode && first.tagName === upperTag && first.attributes.length === 0) {
    while (first.firstChild) {
      editable.insertBefore(first.firstChild, first);
    }
    first.remove();
    return true;
  }

  const wrapper = editable.ownerDocument.createElement(upperTag.toLowerCase());
  while (editable.firstChild) {
    wrapper.appendChild(editable.firstChild);
  }
  editable.appendChild(wrapper);
  return true;
}

function applyBulkCommandToEditable(editable, command, value) {
  if (!editable) return false;
  if (command === 'bold') {
    return toggleWholeInlineTag(editable, 'strong');
  }
  if (command === 'italic') {
    return toggleWholeInlineTag(editable, 'em');
  }
  if (command === 'textColor') {
    const nextToken = String(value || '').trim();
    const nextClass = INLINE_COLOR_CLASS_BY_TOKEN[nextToken] || '';
    const classList = editable.classList;
    if (!classList) return false;
    const existing = INLINE_COLOR_CLASSES.find((cls) => classList.contains(cls)) || '';
    if (!nextClass) return false;
    INLINE_COLOR_CLASSES.forEach((cls) => classList.remove(cls));
    if (existing !== nextClass) {
      classList.add(nextClass);
    }
    if (editable.className.trim() === '') editable.removeAttribute('class');
    return existing !== nextClass || !!existing;
  }
  return false;
}

function applyBulkFormat(command, value = '') {
  if (!activeEdit) return;
  const criteria = resolveBulkCriteria();
  if (!criteria || !criteria.tagName) return;

  const state = store.getState();
  const page = store.getActivePage();
  if (!page) return;
  const pageIndex = state.pages.indexOf(page);
  if (pageIndex < 0) return;

  for (let sectionIndex = 0; sectionIndex < page.sections.length; sectionIndex += 1) {
    const section = page.sections[sectionIndex];
    if (!section?.html) continue;
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${section.html}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    if (!root) continue;

    injectEditableAttrs(root);
    let sectionChanged = false;
    for (const editable of root.querySelectorAll('[data-pb-editable="text"]')) {
      const tagName = (editable.tagName || '').toUpperCase();
      const classSignature = normalizeClassSignature(editable.getAttribute('class') || '');
      if (tagName !== criteria.tagName) continue;
      if (classSignature !== criteria.classSignature) continue;
      if (applyBulkCommandToEditable(editable, command, value)) {
        sectionChanged = true;
      }
    }

    if (!sectionChanged) {
      stripTempAttrs(root);
      continue;
    }

    stripTempAttrs(root);
    store.update(`pages.${pageIndex}.sections.${sectionIndex}.html`, root.innerHTML, 'Edit text');
  }
}

// Format controls live in the text glass panel (sidebar). See glass-panel-text.js.

function createBoundChip() {
  if (boundChip) return;
  boundChip = document.createElement('div');
  boundChip.className = 'pb-bound-chip';
  boundChip.style.display = 'none';
  boundChip.innerHTML = `
    <span class="pb-bound-chip-label" id="pb-bound-chip-label"></span>
    <div class="pb-bound-chip-actions">
      <button type="button" class="pb-bound-chip-btn" data-action="edit-client">Edit in Client/SEO</button>
      <button type="button" class="pb-bound-chip-btn pb-bound-chip-btn-danger" data-action="unbind">Unbind here</button>
    </div>
  `;

  boundChip.addEventListener('pointerdown', (event) => {
    // Mouse only — see the format row in glass-panel-text.js. On touch this
    // preventDefault would suppress the click the buttons below depend on.
    if (event.pointerType && event.pointerType !== 'mouse') return;
    event.preventDefault();
    event.stopPropagation();
  });

  boundChip.addEventListener('click', (event) => {
    const btn = event.target.closest('button[data-action]');
    if (!btn || !activeBoundInfo) return;
    const action = btn.dataset.action;
    if (action === 'edit-client') {
      eventBus.emit('props:request-tab', { tab: 'client-seo' });
      const focusPath = CLIENT_FIELD_PATH_BY_KEY[activeBoundInfo.bindingKey] || null;
      if (focusPath) {
        requestAnimationFrame(() => {
          eventBus.emit('client-seo:focus-field', { path: focusPath });
        });
        setTimeout(() => {
          eventBus.emit('client-seo:focus-field', { path: focusPath });
        }, 120);
      }
      return;
    }
    if (action === 'unbind') {
      unbindActiveBinding();
    }
  });

  document.body.appendChild(boundChip);
}

function removeBoundChip() {
  if (!boundChip) return;
  boundChip.remove();
  boundChip = null;
}

function createPasteTriageToast() {
  if (pasteTriageToast) return;
  pasteTriageToast = document.createElement('div');
  pasteTriageToast.className = 'pb-paste-triage-toast';
  pasteTriageToast.style.display = 'none';
  pasteTriageToast.innerHTML = `
    <div class="pb-paste-triage-message" id="pb-paste-triage-message"></div>
    <div class="pb-paste-triage-actions">
      ${PASTE_PROFILE_SEQUENCE.map(
        (profile) => `
        <button type="button" class="pb-paste-triage-btn" data-profile="${profile}">${PASTE_PROFILE_LABELS[profile]}</button>
      `
      ).join('')}
    </div>
  `;

  pasteTriageToast.addEventListener('click', (event) => {
    const btn = event.target.closest('button[data-profile]');
    if (!btn || !activeEdit) return;
    const profile = btn.dataset.profile;
    if (!PASTE_PROFILE_SEQUENCE.includes(profile)) return;
    bridge.send('replay-last-paste', {
      editSessionId: activeEdit.editSessionId,
      profile
    });
  });

  document.body.appendChild(pasteTriageToast);
}

function removePasteTriageToast() {
  if (_pasteToastHideTimer) {
    clearTimeout(_pasteToastHideTimer);
    _pasteToastHideTimer = null;
  }
  if (!pasteTriageToast) return;
  pasteTriageToast.remove();
  pasteTriageToast = null;
}

function hidePasteTriageToast() {
  if (_pasteToastHideTimer) {
    clearTimeout(_pasteToastHideTimer);
    _pasteToastHideTimer = null;
  }
  if (!pasteTriageToast) return;
  pasteTriageToast.style.display = 'none';
}

function _pasteTriageMessage(state) {
  if (state.rejected) {
    if (state.reason === 'source-too-large') {
      return 'Paste rejected. Clipboard HTML exceeds 1 MB.';
    }
    if (state.reason === 'node-limit-truncated') {
      return 'Paste was too large and had to be truncated.';
    }
    if (state.reason === 'paste-context-mismatch') {
      return 'Cannot reapply profile after caret/context changed.';
    }
    return 'Paste profile could not be applied.';
  }
  if (state.truncated || state.reason === 'node-limit-truncated') {
    return 'Pasted from clipboard. Content was truncated to fit limits.';
  }
  return 'Pasted from clipboard. Choose how formatting should be applied.';
}

function showPasteTriageToast(state) {
  createPasteTriageToast();
  if (!pasteTriageToast) return;

  const msg = pasteTriageToast.querySelector('#pb-paste-triage-message');
  if (msg) msg.textContent = _pasteTriageMessage(state);

  for (const btn of pasteTriageToast.querySelectorAll('button[data-profile]')) {
    btn.classList.toggle('pb-paste-triage-btn-active', btn.dataset.profile === state.profile);
  }

  pasteTriageToast.classList.toggle('pb-paste-triage-toast-error', !!state.rejected);
  pasteTriageToast.style.display = 'flex';

  if (_pasteToastHideTimer) clearTimeout(_pasteToastHideTimer);
  _pasteToastHideTimer = setTimeout(() => {
    _pasteToastHideTimer = null;
    hidePasteTriageToast();
  }, 6000);
}

function positionBoundChip() {
  if (!boundChip || boundChip.style.display === 'none') return false;
  const anchorRect = getSelectionAnchorRect();
  if (!anchorRect) {
    boundChip.style.display = 'none';
    return false;
  }

  const chipRect = boundChip.getBoundingClientRect();
  let left = anchorRect.left;
  let top = anchorRect.bottom + 8;

  if (left + chipRect.width > window.innerWidth - 8) {
    left = window.innerWidth - chipRect.width - 8;
  }
  if (left < 8) left = 8;
  if (top + chipRect.height > window.innerHeight - 8) {
    top = Math.max(8, anchorRect.top - chipRect.height - 8);
  }

  boundChip.style.left = `${left}px`;
  boundChip.style.top = `${top}px`;
  return true;
}

function showBoundChip(binding) {
  if (!binding) {
    hideBoundChip();
    return;
  }
  if (!boundChip) createBoundChip();
  if (!boundChip) return;
  const labelEl = boundChip.querySelector('#pb-bound-chip-label');
  if (labelEl) {
    const label = binding.label || bindingLabelForKey(binding.bindingKey || '');
    labelEl.textContent = `Bound to Client -> ${label}`;
  }
  boundChip.style.display = 'flex';
  if (!positionBoundChip()) {
    hideBoundChip();
  }
}

function hideBoundChip() {
  if (!boundChip) return;
  boundChip.style.display = 'none';
}

function getSelectionAnchorRect() {
  if (_lastSelectionData) {
    const iframe = document.getElementById('pb-preview');
    if (iframe) {
      const iframeRect = iframe.getBoundingClientRect();
      const width = Math.max(0, Number(_lastSelectionData.width || 0));
      const height = Math.max(0, Number(_lastSelectionData.height || 0));
      const centerX = iframeRect.left + Number(_lastSelectionData.x || 0);
      const top = iframeRect.top + Number(_lastSelectionData.y || 0);
      return {
        left: centerX - width / 2,
        right: centerX + width / 2,
        top,
        bottom: top + height,
        width,
        height
      };
    }
  }

  return null;
}

function restoreActiveEditableFocus() {
  if (!activeEdit) return;
  bridge.send('reselect-editable', {
    instanceId: activeEdit.instanceId,
    eid: activeEdit.eid || null,
    editableIndex: activeEdit.editableIndex
  });
}

/**
 * Dispatch a format command from the migrated text glass panel. Mirrors the
 * old floating toolbar's "bulk mode vs single mode" branch: when the bulk
 * checkbox in the panel is on, the same-tag/same-class editables on the page
 * get the format; otherwise only the active selection inside the iframe does.
 */
export function applyFormatCommand(command, value = '') {
  if (_bulkFormattingEnabled) {
    applyBulkFormat(command, value);
    return;
  }
  const payload = { command };
  if (value !== '' && value != null) payload.value = value;
  bridge.send('format-text', payload);
}

export function getLatestSelectionState() {
  return _lastSelectionData ? { ..._lastSelectionData } : null;
}

export function isBulkFormattingEnabled() {
  return _bulkFormattingEnabled;
}

export { handleLinkFormat, openAIRewriteFlow };

function handleLinkFormat() {
  const edit = activeEdit;
  let timer;
  const request = {
    instanceId: edit?.instanceId,
    cancel: () => {
      clearTimeout(timer);
      off();
      _pendingTextRequests.delete(request);
    }
  };
  const finish = (data) => {
    request.cancel();
    if (activeEdit === edit) showLinkPopover(data || null);
  };
  const off = eventBus.on('iframe:active-link-response', finish);
  _pendingTextRequests.add(request);
  timer = setTimeout(() => finish(null), 500);
  bridge.send('query-active-link', {});
}

function isAIRewriteEnabled() {
  const flags = store.getState()?.flags;
  return !!flags?.aiRewrite;
}

function getAIRequestContext() {
  const state = store.getState() || {};
  return {
    sessionId: getAIRewriteSessionId(),
    projectName: state.projectName || 'Untitled',
    clientInfo: state.clientInfo || null,
    brandVoice: state.brandVoice || null,
    flags: state.flags || {}
  };
}

function getAIRewriteSessionId() {
  const key = 'pb-ai-rewrite-session-id';
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const next = crypto.randomUUID();
    localStorage.setItem(key, next);
    return next;
  } catch {
    return crypto.randomUUID();
  }
}

function requestTextEditReply({ responseEvent, command, payload, fallback, timeoutMessage, read }) {
  const edit = activeEdit;
  if (!edit) return Promise.resolve(fallback);
  return new Promise((resolve, reject) => {
    let timeout;
    const request = {
      instanceId: edit.instanceId,
      cancel: () => {
        cleanup();
        resolve(fallback);
      }
    };
    const cleanup = () => {
      clearTimeout(timeout);
      off();
      _pendingTextRequests.delete(request);
    };
    const off = eventBus.on(responseEvent, (data) => {
      if (!data) return;
      if (activeEdit !== edit) return request.cancel();
      if (typeof data.editSessionId === 'number' && data.editSessionId !== edit.editSessionId)
        return;
      if (data.eid && edit.eid && data.eid !== edit.eid) return;
      cleanup();
      resolve(read(data));
    });
    timeout = setTimeout(() => {
      cleanup();
      reject(new Error(timeoutMessage));
    }, 1500);
    _pendingTextRequests.add(request);
    bridge.send(command, { ...payload, editSessionId: edit.editSessionId, eid: edit.eid || null });
  });
}

function requestSelectionSnapshot() {
  return requestTextEditReply({
    responseEvent: 'iframe:selection-snapshot',
    command: 'request-selection-snapshot',
    fallback: { hasSelection: false, text: '', html: '' },
    timeoutMessage: 'Timed out while reading text selection',
    read: (data) => ({
      hasSelection: !!data.hasSelection,
      text: String(data.text || ''),
      html: String(data.html || ''),
      rangeSnapshot: data.rangeSnapshot || null
    })
  });
}

function replaceSelectionHTML(html, rangeSnapshot = null) {
  return requestTextEditReply({
    responseEvent: 'iframe:selection-replaced',
    command: 'replace-selection-html',
    payload: { html: String(html || ''), rangeSnapshot },
    fallback: false,
    timeoutMessage: 'Timed out while applying rewrite',
    read: (data) => !!data.ok
  });
}

async function openAIRewriteFlow() {
  if (!activeEdit) return;
  const edit = activeEdit;
  // These were window.alert. A blocking native dialog is the wrong shape for a
  // "you can't do that yet" message: it stops the whole tab, cannot be styled,
  // and has to be dismissed before the user can go and fix the thing it names.
  // A toast leaves the Text panel reachable, which is where both fixes live.
  if (!isAIRewriteEnabled()) {
    reportError('AI rewrite', new Error('AI rewrite disabled'), {
      user: 'toast',
      message: 'AI rewrite is off. Turn it on in the Text panel settings first.'
    });
    return;
  }
  try {
    const result = await openAIRewriteModal({
      requestSelectionSnapshot,
      replaceSelectionHTML,
      getRequestContext: getAIRequestContext
    });
    if (activeEdit !== edit) return;
    if (result?.reason === 'no-selection') {
      reportError('AI rewrite', new Error('No selection'), {
        user: 'toast',
        message: 'Select some text before running AI rewrite.'
      });
    }
  } catch (error) {
    reportError('AI rewrite', error, {
      user: 'toast',
      message: 'Could not open AI rewrite. Try again.'
    });
  }
}

function showLinkPopover(linkData) {
  const anchorRect = getSelectionAnchorRect();
  if (!anchorRect) return;

  const currentLink = {
    href: typeof linkData?.href === 'string' ? linkData.href : '',
    target: typeof linkData?.target === 'string' ? linkData.target : '',
    rel: typeof linkData?.rel === 'string' ? linkData.rel : ''
  };

  openLinkPopover({
    anchorRect,
    currentLink,
    restoreFocus: restoreActiveEditableFocus,
    onApply: ({ href, openInNewTab }) => {
      const normalizedHref = String(href || '').trim();
      if (!normalizedHref) {
        bridge.send('format-text', { command: 'removeLink' });
      } else {
        bridge.send('format-text', {
          command: 'link',
          value: {
            href: normalizedHref,
            openInNewTab: !!openInNewTab
          }
        });
      }
    },
    onRemove: () => {
      bridge.send('format-text', { command: 'removeLink' });
    }
  });
}

function _normalizeTagName(tagName) {
  const tag = String(tagName || '')
    .trim()
    .toUpperCase();
  return tag || 'TEXT';
}

export function getActiveEdit() {
  return activeEdit;
}

export function getLastCompletedTextEditMeta() {
  return lastCompletedTextEditMeta ? structuredClone(lastCompletedTextEditMeta) : null;
}

// Test-only exports: pure DOM helpers exercised by the text-integrity/race suites.
export {
  restoreProtectedTextChildren as __restoreProtectedTextChildrenForTests,
  sanitizeAndRestoreProtectedTextChildren as __sanitizeAndRestoreProtectedTextChildrenForTests,
  updateEditableInHTML as __updateEditableInHTMLForTests,
  childSignature as __childSignatureForTests
};
